import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import Footer from "../../core/common/footer/Footer";
import StepProgress from "./components/StepProgress";
import CancelAuthentication from "./components/CancelAuthentication";
import useApi from "../../core/customhook/useApi";
import { apibaseurl } from "../../environment";
import { all_routes } from "../router/all_routes";
import SnipperLoader from "../../core/common/loader/SnipperLoader";
import { triggerPackageReload } from "../../core/data/redux/packageSlice";
import { RootState } from "../../core/data/redux/store";

/* =========================================================================
   Shared helpers (module-level, no hooks, safe to reuse anywhere)
========================================================================= */

const getImageUrl = (file: File | null, serverImage?: string | null) => {
  if (file) return URL.createObjectURL(file);
  if (serverImage) return `${apibaseurl}${serverImage}`;
  return "";
};

const validateImage = (file: File | null) => {
  if (!file) {
    Swal.fire({
      icon: "warning",
      title: "Image Required",
      text: "Please select an image.",
    });
    return false;
  }

  const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  if (!allowed.includes(file.type)) {
    Swal.fire({
      icon: "error",
      title: "Invalid File",
      text: "Only JPG, JPEG, PNG and WEBP images are allowed.",
    });
    return false;
  }

  const maxFileSize = 10 * 1024 * 1024; // 10 MB

  if (file.size > maxFileSize) {
    Swal.fire({
      icon: "error",
      title: "File Too Large",
      text: "Maximum file size is 10 MB.",
    });
    return false;
  }

  return true;
};

/* =========================================================================
   useCameraCapture — self-contained camera lifecycle.
   Each card that calls this gets its OWN videoRef/canvasRef/stream,
   so House and Location can never interfere with each other, and
   nothing here ever gets remounted by unrelated parent re-renders.
========================================================================= */

function useCameraCapture(labelForFileName: string) {
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    setCameraError("");
    setCameraReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });

      streamRef.current = stream;
      setShowCamera(true);
    } catch (error: any) {
      console.error(`[${labelForFileName}] Camera error:`, error);

      if (error?.name === "NotAllowedError") {
        setCameraError(
          "Camera permission was denied. Please allow camera access.",
        );
      } else if (error?.name === "NotFoundError") {
        setCameraError("No camera was found on this device.");
      } else {
        setCameraError("Unable to access the camera.");
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setShowCamera(false);
    setCameraReady(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    if (!video.videoWidth || !video.videoHeight) {
      setCameraError("Camera is not ready yet. Please wait.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Unable to capture photo.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Unable to create image.");
          return;
        }

        const capturedFile = new File(
          [blob],
          `${labelForFileName}_${Date.now()}.jpg`,
          {
            type: "image/jpeg",
          },
        );

        setFile(capturedFile);
        stopCamera();
      },
      "image/jpeg",
      0.9,
    );
  };

  useEffect(() => {
    if (!showCamera) return;

    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;

    const handleVideoReady = async () => {
      try {
        await video.play();
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          setCameraReady(true);
        }
      } catch (error) {
        console.error("Video play error:", error);
        setCameraError("Unable to start camera preview.");
      }
    };

    if (video.readyState >= 2) {
      handleVideoReady();
    } else {
      video.onloadedmetadata = handleVideoReady;
    }

    return () => {
      video.onloadedmetadata = null;
    };
  }, [showCamera]);

  // stop the stream if the component unmounts while camera is open
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    showCamera,
    cameraReady,
    cameraError,
    setCameraError,
    file,
    setFile,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    capturePhoto,
  };
}

/* =========================================================================
   HousePhotoCard / LocationPhotoCard
   Top-level components (declared outside DocumentUpload) so React
   never redefines them mid-render. Each owns its own camera state.
========================================================================= */

type CameraCardProps = {
  serverImage?: string | null;
  uploadApi: any; // must expose { callApi, loading }
  addressType?: string;
  onUploaded: () => Promise<void> | void;
};

export const HousePhotoCard: React.FC<CameraCardProps> = ({
  serverImage,
  uploadApi,
  addressType,
  onUploaded,
}) => {
  const cam = useCameraCapture("house");
  const preview = getImageUrl(cam.file, serverImage);

  const handleUpload = async () => {
    if (!validateImage(cam.file)) return;

    const formData = new FormData();
    formData.append("house_photo", cam.file!);
    formData.append("address_type", addressType ?? "");

    try {
      const res = await uploadApi.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "House photo uploaded successfully.",
        });
        cam.setFile(null);
        await onUploaded();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <div className="col-lg-6">
      <div
        className="h-100"
        style={{
          border: "1px solid #eceef3",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="fw-semibold mb-0">House Entry Gate Photo</h6>
            <span className="text-muted ">
              Stand in front of your house entry gate and take a photo.
            </span>
          </div>

          {serverImage && !cam.file && (
            <span className="badge bg-success">
              <i className="ti ti-check me-1"></i>
              Uploaded
            </span>
          )}
        </div>

        {cam.cameraError && (
          <div className="alert alert-danger py-2 text-center">
            {cam.cameraError}
          </div>
        )}

        {cam.showCamera ? (
          <>
            <div className="mb-3">
              <h6>Capture House Entry Gate Photo</h6>
              <small className="text-muted">
                Please stand in front of your house entry gate and take a photo.
              </small>
            </div>

            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                margin: "0 auto",
                background: "#000",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <video
                ref={cam.videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            </div>

            {cam.cameraError && (
              <div className="text-danger text-center mt-2">
                {cam.cameraError}
              </div>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={cam.capturePhoto}
                disabled={!cam.cameraReady}
              >
                <i className="ti ti-camera me-1"></i>
                {cam.cameraReady ? "Capture Photo" : "Starting Camera..."}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cam.stopCamera}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={cam.startCamera}
              >
                <i className="ti ti-camera me-2"></i>
                Open Camera
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={!cam.file || uploadApi.loading}
                onClick={handleUpload}
              >
                {uploadApi.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-upload me-1"></i>
                    Upload
                  </>
                )}
              </button>
            </div>

            <div
              style={{
                height: 200,
                width: 250,
                margin: "auto",
                border: "1px solid #ddd",
                borderRadius: "10px",
                background: "#f8f9fa",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="House Entry Gate Photo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div className="text-center text-muted">
                  <i className="ti ti-camera fs-1"></i>
                  <p className="mb-0">Capture House Entry Gate Photo</p>
                  <small>Camera capture required</small>
                </div>
              )}
            </div>
          </>
        )}

        <canvas ref={cam.canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
};

export const LocationPhotoCard: React.FC<CameraCardProps> = ({
  serverImage,
  uploadApi,
  addressType,
  onUploaded,
}) => {
  const cam = useCameraCapture("locality");
  const preview = getImageUrl(cam.file, serverImage);

  const handleUpload = async () => {
    if (!validateImage(cam.file)) return;

    const formData = new FormData();
    formData.append("locality_photo", cam.file!);
    formData.append("address_type", addressType ?? "");

    try {
      const res = await uploadApi.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "Location photo uploaded successfully.",
        });
        cam.setFile(null);
        await onUploaded();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <div className="col-lg-6">
      <div
        className="h-100"
        style={{
          border: "1px solid #eceef3",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="fw-semibold mb-0">House Building Photo</h6>
            <span className="text-muted ">
              Stand in front of your house building and take a photo.
            </span>
          </div>
          {serverImage && !cam.file && (
            <span className="badge bg-success">
              <i className="ti ti-check me-1"></i>
              Uploaded
            </span>
          )}
        </div>

        {cam.cameraError && (
          <div className="alert alert-danger py-2 text-center">
            {cam.cameraError}
          </div>
        )}
        {cam.showCamera ? (
          <>
            <div className="mb-3">
              <h6>Capture House Building Photo</h6>
              <small className="text-muted">
                Please stand in front of your house building and take a photo.
              </small>
            </div>

            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                margin: "0 auto",
                background: "#000",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <video
                ref={cam.videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            </div>

            {cam.cameraError && (
              <div className="text-danger text-center mt-2">
                {cam.cameraError}
              </div>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={cam.capturePhoto}
                disabled={!cam.cameraReady}
              >
                <i className="ti ti-camera me-1"></i>
                {cam.cameraReady ? "Capture Photo" : "Starting Camera..."}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cam.stopCamera}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={cam.startCamera}
              >
                <i className="ti ti-camera me-2"></i>
                Open Camera
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={!cam.file || uploadApi.loading}
                onClick={handleUpload}
              >
                {uploadApi.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-upload me-1"></i>
                    Upload
                  </>
                )}
              </button>
            </div>

            <div
              style={{
                height: 200,
                width: 250,
                margin: "auto",
                border: "1px solid #ddd",
                borderRadius: "10px",
                background: "#f8f9fa",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="House Building Photo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div className="text-center text-muted">
                  <i className="ti ti-camera fs-1"></i>
                  <p className="mb-0">Capture House Building Photo</p>
                  <small>Camera capture required</small>
                </div>
              )}
            </div>
          </>
        )}

        <canvas ref={cam.canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
};

/* =========================================================================
   DocumentUpload — parent page
========================================================================= */

const DocumentUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxDashboardData = useSelector(
    (state: any) => state.dashboard.dashboardData,
  );

  const candidateId = reduxDashboardData?.assignmentDetails?.candidate_id;
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);
  /* ---------------- GET API ---------------- */

  const getAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  /* ---------------- Upload APIs ---------------- */

  const uploadIdAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/id-proof-photo`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const uploadAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/address-proof-photo`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const uploadHouseAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/house-photo`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const uploadLocationAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/locality-photo`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const submitAddressApi = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}/address-check-in-progress`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  /* ---------------- States ---------------- */

  const [detailData, setDetailData] = useState<any>(null);
  const [confirmSubmission, setConfirmSubmission] = useState(true);

  // Only ID / Address use plain file-input upload now.
  // House / Location are handled entirely inside HousePhotoCard / LocationPhotoCard.
  const [files, setFiles] = useState({
    id: null as File | null,
    address: null as File | null,
  });

  /* ---------------- Fetch ---------------- */

  const fetchAddressDetails = async () => {
    try {
      const res = await getAddressAPI.callApi();

      if (res?.success) {
        setDetailData(res.data?.[0] ?? null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (candidateId) {
      fetchAddressDetails();
    }
  }, [candidateId]);

  /* ---------------- Select File ---------------- */

  const handleFileChange = (key: keyof typeof files, file: File | null) => {
    setFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  /* ---------------- Upload (ID / Address) ---------------- */

  const uploadDocument = async ({
    file,
    api,
    field,
    fieldName,
  }: {
    file: File | null;
    api: any;
    field: keyof typeof files;
    fieldName: string;
  }) => {
    if (!validateImage(file)) return;

    const formData = new FormData();

    formData.append(fieldName, file!);
    formData.append("address_type", detailData?.address_type ?? "");

    try {
      const res = await api.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "Document uploaded successfully.",
        });

        setFiles((prev) => ({
          ...prev,
          [field]: null,
        }));

        await fetchAddressDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  /* ---------------- Submit ---------------- */

  const handleSubmit = async () => {
    if (!detailData?.id_proof_photo || !detailData?.address_proof_photo) {
      Swal.fire({
        icon: "warning",
        title: "Documents Required",
        text: "Please upload all four documents.",
      });
      return;
    }
    // if (
    //   !detailData?.id_proof_photo ||
    //   !detailData?.address_proof_photo ||
    //   !detailData?.house_photo ||
    //   !detailData?.locality_photo
    // ) {
    //   Swal.fire({
    //     icon: "warning",
    //     title: "Documents Required",
    //     text: "Please upload all four documents.",
    //   });
    //   return;
    // }

    if (!confirmSubmission) {
      Swal.fire({
        icon: "warning",
        title: "Confirmation Required",
        text: "Please accept the declaration.",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Submit Address Verification?",
      text: "Are you sure you want to submit this address verification?",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await submitAddressApi.callApi({});

      if (response?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.message || "Address verification submitted successfully.",
        });
        dispatch(triggerPackageReload());
        navigate(all_routes.addressInfo);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: response?.message || "Something went wrong.",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  /* ---------------- Upload Card (ID / Address, unchanged) ---------------- */

  type UploadCardProps = {
    title: string;
    fileKey: keyof typeof files;
    fieldName: string;
    serverImage: string | null | undefined;
    uploadApi: any;
    accept?: string;
  };

  const UploadCard = ({
    title,
    fileKey,
    fieldName,
    serverImage,
    uploadApi,
    accept = "image/*",
  }: UploadCardProps) => {
    const preview = getImageUrl(files[fileKey], serverImage);

    return (
      <div className="col-lg-6">
        <div
          className="h-100"
          style={{
            border: "1px solid #eceef3",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-semibold mb-0">
              {title}
              <span className="text-danger ms-1">*</span>
            </h6>

            {serverImage && (
              <span className="badge bg-success">
                <i className="ti ti-check me-1"></i>
                Uploaded
              </span>
            )}
          </div>

          <div className="d-flex gap-2 mb-3">
            <input
              key={files[fileKey] ? undefined : `${fileKey}-empty`}
              type="file"
              className="form-control"
              accept={accept}
              onChange={(e) =>
                handleFileChange(fileKey, e.target.files?.[0] || null)
              }
            />

            <button
              className="btn btn-primary flex-shrink-0"
              disabled={!files[fileKey] || uploadApi.loading}
              onClick={() =>
                uploadDocument({
                  file: files[fileKey],
                  api: uploadApi,
                  field: fileKey,
                  fieldName,
                })
              }
            >
              {uploadApi.loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="ti ti-upload me-1"></i>
                  Upload
                </>
              )}
            </button>
          </div>

          <div
            style={{
              height: 200,
              width: 250,
              margin: "auto",
              border: "1px solid #ddd",
              borderRadius: "10px",
              background: "#f8f9fa",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {preview ? (
              <img
                src={preview}
                alt={title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div className="text-center text-muted">
                <i className="ti ti-photo fs-1"></i>
                <p className="mb-0">No Image Selected</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const templates = useSelector((state: RootState) => state.package.templates);

  const aadharTemplate = templates.find(
    (item: any) => item.check_name === "Address Verification",
  );

  const assignmentTemplateId = aadharTemplate?.assignment_template_id;
  const caseId = aadharTemplate?.case_id;
  const assignmentId = aadharTemplate?.assignment_id;
  const { state } = useLocation();
  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const handleSubmitReply = async () => {
    const finalAssignmentTemplateId =
      assignment_template_id ?? assignmentTemplateId;

    const finalAssignmentId = assignment_id ?? assignmentId;

    const finalCaseId = case_id ?? caseId;

    const finalCandidateId = candidate_id ?? candidateId;

    if (!finalAssignmentTemplateId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Assignment Template",
        text: "Assignment template information is missing.",
      });
      return;
    }

    if (!finalAssignmentId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Assignment",
        text: "Assignment information is missing.",
      });
      return;
    }

    if (!finalCaseId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Case",
        text: "Case information is missing.",
      });
      return;
    }

    if (!finalCandidateId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Candidate",
        text: "Candidate information is missing.",
      });
      return;
    }

    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });
      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        String(finalAssignmentTemplateId),
      );

      payload.append("assignment_id", String(finalAssignmentId));

      payload.append("case_id", String(finalCaseId));

      payload.append("candidate_id", String(finalCandidateId));

      payload.append("actor_due_on", actor_type || "CANDIDATE");

      payload.append("remarks", replyRemarks.trim());

      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        setReplyRemarks("");
        setReplyDocuments([]);
        setShowReplyModal(false);

        navigate(all_routes.caseSummary);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: res?.error || res?.message || "Unable to submit your response.",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit your response.",
      });
    }
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {getAddressAPI.loading ? (
          <SnipperLoader />
        ) : (
          <>
            <StepProgress
              totalSteps={5}
              currentStep={5}
              completedSteps={[1, 2, 3, 4]}
            />
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0 fw-semibold">Documents Upload</h5>
              </div>

              <div className="card-body pb-3">
                <div
                  style={{
                    backgroundColor: "#fdeaea",
                    border: "1px solid #f6cdd0",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    textAlign: "center",
                  }}
                >
                  <p
                    className="fw-semibold text-center mb-0"
                    style={{ color: "#c0392b", fontSize: "13.5px" }}
                  >
                    Kindly upload an image in <strong>JPG, JPEG, PNG</strong>{" "}
                    format only. PDF and other file types are not allowed.
                    Maximum file size: <strong>10 MB</strong>.
                  </p>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="row g-4">
                  <UploadCard
                    title="ID Photo"
                    fileKey="id"
                    fieldName="id_proof_photo"
                    serverImage={detailData?.id_proof_photo}
                    uploadApi={uploadIdAPI}
                    accept=".jpg,.jpeg,.png,.webp,image/*"
                  />

                  <UploadCard
                    title="Address Proof"
                    fileKey="address"
                    fieldName="address_proof_photo"
                    serverImage={detailData?.address_proof_photo}
                    uploadApi={uploadAddressAPI}
                  />
                </div>

                <div className="mt-4 mb-3">
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-2 mb-3 rounded-2"
                    style={{
                      background: "#FFF8F1",
                      border: "1px solid #FFD8A8",
                      borderLeft: "4px solid #E67E22",
                    }}
                  >
                    <i
                      className="ti ti-camera fs-3"
                      style={{ color: "#E67E22" }}
                    ></i>

                    <span className="text-dark">
                      <strong>Camera Capture Required:</strong> House and
                      Location photos must be captured using the{" "}
                      <strong>device camera</strong>. Existing photos cannot be
                      uploaded.
                    </span>
                  </div>
                </div>

                <div className="row g-4">
                  <HousePhotoCard
                    serverImage={detailData?.house_photo}
                    uploadApi={uploadHouseAPI}
                    addressType={detailData?.address_type}
                    onUploaded={fetchAddressDetails}
                  />

                  <LocationPhotoCard
                    serverImage={detailData?.locality_photo}
                    uploadApi={uploadLocationAPI}
                    addressType={detailData?.address_type}
                    onUploaded={fetchAddressDetails}
                  />
                </div>

                {/* Declaration */}

                <div
                  className="mt-4"
                  style={{
                    border: "1px solid #E8EBEF",
                    borderRadius: "12px",
                    background: "#FAFBFC",
                    padding: "18px 20px",
                  }}
                >
                  <div className="d-flex align-items-start">
                    <input
                      id="confirmSubmission"
                      type="checkbox"
                      className="form-check-input mt-1 me-3"
                      checked={confirmSubmission}
                      onChange={(e) => setConfirmSubmission(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />

                    <div>
                      <h6 className="fw-semibold mb-2">
                        Declaration
                        <span className="text-danger ms-1">*</span>
                      </h6>

                      <label
                        htmlFor="confirmSubmission"
                        className="mb-0"
                        style={{
                          fontSize: "14px",
                          color: "#495057",
                          lineHeight: "1.7",
                          cursor: "pointer",
                        }}
                      >
                        I hereby declare that all the documents submitted are
                        authentic, belong to me, and have not been altered or
                        tampered with. I understand that providing false or
                        misleading information may result in rejection of my
                        verification/application.
                      </label>
                    </div>
                  </div>
                </div>

                {/* Buttons */}

                <div className="d-flex justify-content-end gap-3 mt-4">
                  {detailData?.status === "CANDIDATE_INSUFF" ? (
                    <div className="d-flex gap-2 align-items-center mt-5">
                      <button
                        onClick={() => navigate(-1)}
                        className="btn btn-outline-primary"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => setShowReplyModal(true)}
                      >
                        <i className="ti ti-message-circle me-2"></i>
                        Reply to Insufficiency
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={submitAddressApi.loading}
                      >
                        <i className="ti ti-upload me-2"></i>
                        Save Form & Documents
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex gap-2 align-items-center mt-5">
                      <button
                        onClick={() => navigate(-1)}
                        className="btn btn-outline-primary"
                      >
                        Back
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={submitAddressApi.loading}
                      >
                        {submitAddressApi.loading ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {showReplyModal && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,.5)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                {/* Header */}
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">
                      Respond to Insufficiency
                    </h5>

                    <small className="text-muted">
                      Please provide your explanation and upload supporting
                      documents if applicable.
                    </small>
                  </div>

                  <button
                    className="btn-close"
                    onClick={() => setShowReplyModal(false)}
                  />
                </div>

                {/* Body */}
                <div className="modal-body">
                  {/* Remarks */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Remarks
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <textarea
                      rows={5}
                      className="form-control"
                      placeholder="Enter your response to the verifier..."
                      value={replyRemarks}
                      onChange={(e) => setReplyRemarks(e.target.value)}
                      style={{ resize: "none" }}
                    />

                    <small className="text-muted">
                      Clearly explain the requested information or
                      clarification.
                    </small>
                  </div>

                  {/* Upload */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Supporting Documents
                    </label>

                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleReplyFileChange}
                    />

                    <small className="text-muted">
                      Accepted formats: JPG, JPEG, PNG, PDF (Maximum 10 MB per
                      file)
                    </small>
                  </div>

                  {/* Selected Files */}
                  {replyDocuments.length > 0 && (
                    <div className="mt-3">
                      <label className="fw-semibold mb-2">Selected Files</label>

                      <div className="d-flex flex-wrap gap-2">
                        {replyDocuments.map((file, index) => (
                          <span
                            key={index}
                            className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                          >
                            <i className="ti ti-paperclip me-2"></i>

                            <span
                              className="text-truncate"
                              style={{ maxWidth: 180 }}
                            >
                              {file.name}
                            </span>

                            <button
                              type="button"
                              className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                              onClick={() => removeReplyFile(index)}
                            >
                              <i className="ti ti-x"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="modal-footer gap-3">
                  <button
                    className="btn btn-light border"
                    onClick={() => setShowReplyModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitReply}
                    disabled={insuffResponse.loading}
                  >
                    {insuffResponse.loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send me-2"></i>
                        Submit Response
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default DocumentUpload;
